<template>
  <div class="methanogen-list">
    <!-- 头部区域 -->
    <header class="hero-section">
      <div class="carousel-container">
        <el-carousel height="600px" :interval="5000" arrow="always">
          <el-carousel-item v-for="(image, index) in carouselImages" :key="index">
            <div class="carousel-content" :style="image">
              <div class="hero-content">
                <h1 class="main-title">
                  Methanogenic Archaeal Genome Database
                </h1>
                <p class="subtitle">
                  A Comprehensive Database for Methanogenic Archaea
                </p>
                <div class="search-container">
                  <div class="search-box">
                    <el-input
                      v-model="searchQuery"
                      placeholder="Search by species name, Assembly ID or Assembly Accession..."
                      class="search-input"
                      @keyup.enter="handleSearch"
                    >
                      <template #append>
                        <el-button @click="handleSearch">Search</el-button>
                      </template>
                    </el-input>
                  </div>
                </div>
              </div>
            </div>
          </el-carousel-item>
        </el-carousel>
      </div>
    </header>

    <!-- 数据概览区域 -->
    <section class="stats-section">
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-icon">
            <el-icon><DataLine /></el-icon>
          </div>
          <div class="stat-content">
            <h3>Genome Data</h3>
            <p class="stat-number">{{ methanogens.length }}</p>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon">
            <el-icon><DataLine /></el-icon>
          </div>
          <div class="stat-content">
            <h3>Plasmid Data</h3>
            <p class="stat-number">{{ plasmidCount }}</p>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon">
            <el-icon><DataLine /></el-icon>
          </div>
          <div class="stat-content">
            <h3>Phage Data</h3>
            <p class="stat-number">{{ virusCount }}</p>
          </div>
        </div>
      </div>
    </section>
    
    <!-- 分布地图区域 -->
    <world-map />

    <!-- 数据表格区域 -->
    <section class="table-section">
    <div class="table-container">
      <div class="table-header">
        <h2>Genome Data</h2>
        <div class="table-actions">
          <el-button-group>
            <el-button :icon="View" @click="showTomeOGTSelection">Batch Tome OGT</el-button>
            <el-button :icon="Download" @click="showBatchDownloadDialog">Batch Export</el-button>
          </el-button-group>
        </div>
      </div>
      <div class="toolbar">
        <div class="search-box">
          <el-input
            v-model="searchQuery"
            placeholder="Search genomes..."
            :prefix-icon="Search"
            clearable
            @input="handleSearch"
          />
        </div>
        <div class="filter-options">
          <span class="filter-label">Assembly Level:</span>
          <el-radio-group v-model="levelFilter" @change="applyFilters" size="small">
            <el-radio-button label="All">All</el-radio-button>
            <el-radio-button label="Complete Genome">Complete Genome</el-radio-button>
            <el-radio-button label="Chromosome">Chromosome</el-radio-button>
            <el-radio-button label="Scaffold">Scaffold</el-radio-button>
            <el-radio-button label="Contig">Contig</el-radio-button>
          </el-radio-group>
        </div>
      </div>
      <el-table 
        v-loading="loading"
        :data="paginatedData" 
        style="width: 100%"
        :header-cell-style="headerStyle"
        :cell-style="cellStyle"
        height="950"
        @selection-change="handleSelectionChange"
        @sort-change="handleSortChange"
        ref="genomeDataSection"
      >
        <el-table-column
          v-if="showSelection"
          type="selection"
          width="55"
        />
        
        <el-table-column 
          type="index" 
          label="ID" 
          width="70" 
          align="center"
        />

        <el-table-column type="expand">
            <template #default="props">
              <div class="detail-expand">
                <div class="detail-section">
                  <h4>Strain Information</h4>
                  <p>{{ getStrainInfo(props.row) }}</p>
                </div>
                <div class="detail-section">
                  <h4>Assembly Details</h4>
                  <p>Level: {{ props.row.assembly_level }}</p>
                  <p>Date: {{ formatDate(props.row.assembly_release_date) }}</p>
                </div>
              </div>
            </template>
          </el-table-column>

          <el-table-column prop="assembly_name" label="Assembly Name" min-width="180">
            <template #default="scope">
              <a class="detail-link" @click="viewDetail(scope.row.assembly_accession)">
                {{ scope.row.assembly_name }}
              </a>
            </template>
          </el-table-column>

          <el-table-column prop="assembly_accession" label="Assembly Accession" min-width="150">
            <template #default="scope">
              <a class="detail-link" @click="viewDetail(scope.row.assembly_accession)">
                {{ scope.row.assembly_accession }}
              </a>
            </template>
          </el-table-column>

          <el-table-column prop="organism_name" label="Organism Name" min-width="200">
            <template #default="scope">
              <div class="organism-name">
                <a class="detail-link" @click="viewDetail(scope.row.assembly_accession)">
                  {{ scope.row.organism_name }}
                </a>
                <el-tag 
                  size="small" 
                  :type="getAssemblyLevelType(scope.row.assembly_level)"
                  class="level-tag"
                >
                  {{ scope.row.assembly_level }}
                </el-tag>
              </div>
            </template>
          </el-table-column>

          <el-table-column prop="organism_infraspecific_names_strain" label="Strain" min-width="120" />
          <el-table-column prop="annotation_name" label="Annotation Name" min-width="180" />
          <el-table-column 
            prop="assembly_level" 
            label="Assembly Level" 
            min-width="130"
            sortable="custom"
          >
            <template #default="scope">
              <span>{{ scope.row.assembly_level }}</span>
            </template>
          </el-table-column>
          <el-table-column 
            prop="assembly_release_date" 
            label="Release Date" 
            min-width="120"
            sortable="custom"
          >
            <template #default="scope">
              <span>{{ formatDate(scope.row.assembly_release_date) }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="wgs_project_accession" label="WGS Project" min-width="130" />
          <el-table-column 
            prop="assembly_stats_number_of_scaffolds" 
            label="Scaffolds" 
            width="100" 
            align="right"
          >
            <template #default="scope">
              <span>{{ formatNumber(scope.row.assembly_stats_number_of_scaffolds) }}</span>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-container">
          <el-pagination
            v-model:current-page="currentPage"
            v-model:page-size="pageSize"
            :page-sizes="[10, 20, 50, 100,500]"
            layout="total, sizes, prev, pager, next, jumper"
            :total="filteredMethanogens.length"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </div>
    </section>

    <!-- 添加弹出对话框来显示详细信息 -->
    <el-dialog
      v-model="dialogVisible"
      title="Strain Details"
      width="80%"
      :destroy-on-close="true"
      @close="handleDialogClose"
    >
      <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>
      <strain-detail
        v-if="dialogVisible && selectedAssemblyId"
        :assembly-accession="selectedAssemblyId"
        :key="selectedAssemblyId"
        @error="showError"
      />
    </el-dialog>

    <!-- 添加批量下载对话框 -->
    <el-dialog
      v-model="batchDownloadDialogVisible"
      title="Batch Export"
      width="400px"
      :close-on-click-modal="false"
    >
      <div class="batch-download-content">
        <h3 class="download-title">Select File Types</h3>
        <el-checkbox-group v-model="selectedFileTypes" class="download-options">
          <el-checkbox label="fasta">Genome Sequence (FASTA)</el-checkbox>
          <el-checkbox label="gbff">Sequence and Annotation (GBFF)</el-checkbox>
          <el-checkbox label="faa">Protein Sequences (FAA)</el-checkbox>
        </el-checkbox-group>
      </div>
      
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="batchDownloadDialogVisible = false">Cancel</el-button>
          <el-button
            type="primary"
            :loading="batchDownloading"
            :disabled="selectedFileTypes.length === 0"
            @click="startBatchDownload"
          >
            Start Download
          </el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 添加Tome OGT分析对话框 -->
    <el-dialog
      v-model="tomeOGTDialogVisible"
      title="Tome OGT Analysis"
      width="450px"
      :close-on-click-modal="false"
    >
      <div class="tome-ogt-content">
        <h3 class="analysis-title">Optimal Growth Temperature Prediction</h3>
        <p>Will run Tome OGT analysis on protein sequences (FAA) of the selected strains.</p>
        <p v-if="selectedRows.length > 0" class="selected-count">
          Selected {{ selectedRows.length }} strain(s)
        </p>
        <el-alert
          v-if="selectedRows.length > 5"
          title="Processing multiple strains may take some time."
          type="warning"
          :closable="false"
          show-icon
        />
      </div>
      
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="tomeOGTDialogVisible = false">Cancel</el-button>
          <el-button
            type="primary"
            :loading="tomeAnalyzing"
            @click="startTomeOGTAnalysis"
          >
            Start Analysis
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { 
  Search,
  Calendar,
  Download,
  View,
  DataLine 
} from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import axios from 'axios'
import StrainDetail from './StrainDetail.vue'
import WorldMap from './WorldMap.vue'

export default {
  name: 'MethanogenList',
  components: {
    StrainDetail,
    WorldMap
  },
  setup() {
    const searchQuery = ref('')
    const methanogens = ref([])
    const loading = ref(false)
    const currentPage = ref(1)
    const pageSize = ref(10)
    const isScrolled = ref(false)
    const dialogVisible = ref(false)
    const selectedAssemblyId = ref(null)
    const batchDownloadDialogVisible = ref(false)
    const selectedFileTypes = ref([])
    const batchDownloading = ref(false)
    const showSelection = ref(false)
    const selectedRows = ref([])
    const currentSection = ref('home')
    const genomeDataSection = ref(null)
    const tomeOGTDialogVisible = ref(false)
    const tomeAnalyzing = ref(false)
    // 添加质粒和病毒数据计数
    const plasmidCount = ref(0)
    const virusCount = ref(0)

    const carouselImages = ref([
      { 
        backgroundImage: 'url(/images/soil.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      },
      { 
        backgroundImage: 'url(/images/cattle.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      },
      { 
        backgroundImage: 'url(/images/volcano.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }
    ])

    // 添加排序相关的状态
    const sortBy = ref('');
    const sortOrder = ref('');

    // 添加过滤后的数据计算属性
    const filteredMethanogens = computed(() => {
      let result = [...methanogens.value];
      
      // 应用搜索过滤
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        result = result.filter(item => 
          item.assembly_name?.toLowerCase().includes(query) ||
          item.assembly_accession?.toLowerCase().includes(query) ||
          item.organism_name?.toLowerCase().includes(query) ||
          item.organism_infraspecific_names_strain?.toLowerCase().includes(query)
        );
      }
      
      // 应用组装等级过滤
      if (levelFilter.value !== 'All') {
        result = result.filter(item => item.assembly_level === levelFilter.value);
      }
      
      // 应用排序
      if (sortBy.value && sortOrder.value) {
        result.sort((a, b) => {
          let valueA = a[sortBy.value];
          let valueB = b[sortBy.value];
          
          // 特殊处理日期字段
          if (sortBy.value === 'assembly_release_date') {
            valueA = new Date(valueA || '1900-01-01');
            valueB = new Date(valueB || '1900-01-01');
          }
          
          // 特殊处理 Assembly Level 字段
          if (sortBy.value === 'assembly_level') {
            const levelPriority = {
              'Complete Genome': 1,
              'Chromosome': 2,
              'Scaffold': 3,
              'Contig': 4
            };
            valueA = levelPriority[valueA] || 999;
            valueB = levelPriority[valueB] || 999;
          }
          
          if (valueA < valueB) return sortOrder.value === 'ascending' ? -1 : 1;
          if (valueA > valueB) return sortOrder.value === 'ascending' ? 1 : -1;
          return 0;
        });
      }
      
      return result;
    });

    // 分页数据计算属性
    const paginatedData = computed(() => {
      const start = (currentPage.value - 1) * pageSize.value;
      const end = start + pageSize.value;
      return filteredMethanogens.value.slice(start, end);
    });

    // 加载基因组数据的函数
    const loadGenomeData = async () => {
      try {
        const response = await axios.get('/api/mash-data/?page=1&page-size=30');
        if (response.data && response.data.items) {
          // 获取数据后应用默认排序，将Complete Genome排在最前面
          methanogens.value = response.data.items.sort((a, b) => {
            // 定义组装等级优先级
            const levelPriority = {
              'Complete Genome': 1,
              'Chromosome': 2,
              'Scaffold': 3,
              'Contig': 4
            };
            
            // 获取组装等级优先级，如果没有则给高值
            const levelA = levelPriority[a.assembly_level] || 999;
            const levelB = levelPriority[b.assembly_level] || 999;
            
            // 按优先级排序
            return levelA - levelB;
          });
          
          // 设置默认排序为assembly_level
          sortBy.value = 'assembly_level';
          sortOrder.value = 'ascending';
        } else {
          console.error('Invalid response format:', response.data);
        }
      } catch (error) {
        console.error('Error loading genome data:', error);
      }
    };

    // 加载质粒数据总数
    const loadPlasmidCount = async () => {
      try {
        const response = await axios.get('/api/plasmid-data/', {
          params: {
            page: 1,
            page_size: 1 // 只需要获取总数，不需要具体数据
          }
        });
        if (response.data && response.data.total !== undefined) {
          plasmidCount.value = response.data.total;
        }
      } catch (error) {
        console.error('Error loading plasmid count:', error);
        // 如果API不存在或出错，保持默认值0
        plasmidCount.value = 0;
      }
    };

    // 加载病毒数据总数
    const loadVirusCount = async () => {
      try {
        const response = await axios.get('/api/virus-data/', {
          params: {
            page: 1,
            page_size: 1 // 只需要获取总数，不需要具体数据
          }
        });
        if (response.data && response.data.total !== undefined) {
          virusCount.value = response.data.total;
        }
      } catch (error) {
        console.error('Error loading virus count:', error);
        // 如果API不存在或出错，保持默认值0
        virusCount.value = 0;
      }
    };

    // 加载所有数据的函数
    const loadData = async () => {
      loading.value = true;
      try {
        // 并行加载所有数据
        await Promise.all([
          loadGenomeData(),
          loadPlasmidCount(),
          loadVirusCount()
        ]);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        loading.value = false;
      }
    };

    // 搜索处理函数
    const handleSearch = () => {
      currentPage.value = 1;
      // 当搜索内容不为空时，滚动到表格区域
      if (searchQuery.value.trim()) {
        nextTick(() => {
          const tableSection = document.querySelector('.table-section');
          if (tableSection) {
            const offset = 80;  // 导航栏高度
            const elementPosition = tableSection.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;
            
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }
        });
      }
    };

    // 快速搜索函数
    const quickSearch = (term) => {
      if (term === 'Latest Data') {
        searchQuery.value = '';
        sortBy.value = 'assembly_release_date';
        sortOrder.value = 'descending';
        currentPage.value = 1;
        nextTick(() => {
          const tableSection = document.querySelector('.table-section');
          if (tableSection) {
            const offset = 80;
            const elementPosition = tableSection.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;
            
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }
        });
      } else {
        searchQuery.value = term;
        handleSearch();
      }
    };

    // 页面大小改变处理函数
    const handleSizeChange = (val) => {
      pageSize.value = val;
      currentPage.value = 1;
    };

    // 当前页改变处理函数
    const handleCurrentChange = (val) => {
      currentPage.value = val;
    };

    // 获取当前日期
    const getCurrentDate = () => {
      return new Date().toLocaleDateString();
    };

    // 格式化日期
    const formatDate = (date) => {
      if (!date) return 'Unknown';
      return new Date(date).toLocaleDateString();
    };

    // 格式化数字
    const formatNumber = (num) => {
      if (num === null || num === undefined) return 'Unknown';
      return num.toLocaleString();
    };

    // 获取菌株信息
    const getStrainInfo = (row) => {
      return row.organism_infraspecific_names_strain || 'Unknown';
    };

    // 获取Assembly Level的标签类型
    const getAssemblyLevelType = (level) => {
      const types = {
        'Complete Genome': 'success',
        'Chromosome': 'warning',
        'Scaffold': 'info',
        'Contig': ''
      };
      return types[level] || '';
    };

    // 查看详情
    const viewDetail = async (assemblyId) => {
      try {
        selectedAssemblyId.value = assemblyId;
        dialogVisible.value = true;
        
        // 记录到控制台以便调试
        console.log('View detail:', {
          assemblyId,
          selectedAssemblyId: selectedAssemblyId.value,
          dialogVisible: dialogVisible.value
        });
      } catch (error) {
        console.error('Error viewing details:', error);
        showError('Failed to load details, please try again');
      }
    };

    // 添加错误提示组件
    const errorMessage = ref('');
    const showError = (message) => {
      errorMessage.value = message;
      ElMessage.error('Failed to load details, please try again');
      setTimeout(() => {
        errorMessage.value = '';
      }, 3000);
    };

    // 修改弹窗关闭处理
    const handleDialogClose = () => {
      selectedAssemblyId.value = null;
      dialogVisible.value = false;
      // 如果是批量下载对话框关闭，不要隐藏选择框
      if (!batchDownloadDialogVisible.value) {
        showSelection.value = false;
        selectedRows.value = [];
      }
    };

    // 表格样式
    const headerStyle = {
      background: '#f5f7fa',
      color: '#606266',
      fontWeight: 'bold'
    };

    const cellStyle = {
      color: '#606266'
    };

    // 监听滚动事件
    const handleScroll = () => {
      isScrolled.value = window.scrollY > 50;
    };

    // 处理表格选择变化
    const handleSelectionChange = (selection) => {
      selectedRows.value = selection;
    };

    // 修改显示批量下载对话框函数
    const showBatchDownloadDialog = () => {
      if (!showSelection.value) {
        // 如果还没有显示选择框，就显示选择框
        showSelection.value = true;
        selectedFileTypes.value = [];
        ElMessage({
          message: 'Please select strains to download',
          type: 'info'
        });
      } else if (selectedRows.value.length === 0) {
        // 如果已经显示选择框但没有选择任何菌株
        ElMessage.warning('Please select at least one strain');
      } else {
        // 如果已经选择了菌株，显示下载对话框
        batchDownloadDialogVisible.value = true;
      }
    };

    // 修改开始批量下载函数
    const startBatchDownload = async () => {
      if (selectedFileTypes.value.length === 0) {
        ElMessage.warning('Please select at least one file type');
        return;
      }

      if (selectedRows.value.length === 0) {
        ElMessage.warning('Please select at least one strain');
        return;
      }

      batchDownloading.value = true;
      const totalFiles = selectedRows.value.length * selectedFileTypes.value.length;
      let successCount = 0;
      let failCount = 0;

      try {
        // 显示开始下载提示
        ElMessage({
          message: `Starting to download ${totalFiles} files...`,
          type: 'info',
          duration: 3000
        });

        // 为每个选中的基因组和文件类型创建下载任务
        for (const genome of selectedRows.value) {
          for (const fileType of selectedFileTypes.value) {
            try {
              // 显示当前下载进度
              const currentProgress = successCount + failCount + 1;
              const progressMessage = `Downloading file ${currentProgress}/${totalFiles}: ${genome.assembly_accession}_${fileType}`;
              
              ElMessage({
                message: progressMessage,
                type: 'info',
                duration: 2000
              });

              const response = await axios.get(
                `/api/genome/${genome.assembly_accession}/download/${fileType}/`,
                { 
                  responseType: 'blob',
                  timeout: 300000, // 5分钟超时
                  onDownloadProgress: (progressEvent) => {
                    if (progressEvent.total) {
                      const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                      // 更新下载进度
                      ElMessage({
                        message: `${progressMessage} (${percentCompleted}%)`,
                        type: 'info',
                        duration: 1000
                      });
                    }
                  }
                }
              );

              // 检查响应类型
              const contentType = response.headers['content-type'];
              if (contentType && contentType.includes('application/json')) {
                // 如果服务器返回了JSON格式的错误信息
                const reader = new FileReader();
                reader.onload = () => {
                  const errorData = JSON.parse(reader.result);
                  throw new Error(errorData.error || 'Download failed');
                };
                reader.readAsText(response.data);
                return;
              }

              // 创建下载链接
              const url = window.URL.createObjectURL(new Blob([response.data], { 
                type: 'application/zip' 
              }));
              const link = document.createElement('a');
              link.href = url;
              // 使用生物体名称作为文件名，替换掉可能导致文件名无效的字符
              const safeOrganismName = genome.organism_name
                .replace(/[<>:"/\\|?*]/g, '_') // 替换Windows不允许的文件名字符
                .replace(/\s+/g, '_') // 将空格替换为下划线
                .replace(/[^\w\-._]/g, '_'); // 替换其他任何不安全的字符
              link.setAttribute('download', `${safeOrganismName}_${fileType}.zip`);
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);

              successCount++;
              ElMessage({
                message: `${genome.organism_name}_${fileType} downloaded successfully`,
                type: 'success',
                duration: 2000
              });

            } catch (error) {
              failCount++;
              console.error('Download failed:', error);
              
              let errorMessage = 'Download failed';
              if (error.response) {
                const contentType = error.response.headers['content-type'];
                if (contentType && contentType.includes('application/json')) {
                  try {
                    const errorData = JSON.parse(await error.response.data.text());
                    errorMessage = errorData.error || errorMessage;
                  } catch (e) {
                    errorMessage = `Download failed: ${error.response.status}`;
                  }
                } else {
                  errorMessage = `Download failed: ${error.response.status}`;
                }
              } else if (error.message) {
                errorMessage = error.message;
              }

              ElMessage({
                message: `${genome.assembly_accession}_${fileType}: ${errorMessage}`,
                type: 'error',
                duration: 5000
              });
            }
          }
        }

        // 显示下载结果
        if (failCount === 0) {
          ElMessage.success(`All ${totalFiles} files downloaded successfully`);
        } else {
          ElMessage.warning(`${successCount}/${totalFiles} files downloaded successfully, ${failCount} files failed`);
        }

        // 下载完成后，隐藏选择框并清空选择
        showSelection.value = false;
        selectedRows.value = [];
      } catch (error) {
        console.error('Batch download error:', error);
        ElMessage.error('Batch download failed, please try again');
      } finally {
        batchDownloading.value = false;
        batchDownloadDialogVisible.value = false;
      }
    };

    // 修改滚动函数
    const scrollToDatabase = () => {
      const tableSection = document.querySelector('.table-section');
      if (tableSection) {
        const offset = 80; // 添加偏移量，为导航栏预留空间
        const elementPosition = tableSection.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    };

    // 修改滚动监听函数
    const handlePageScroll = () => {
      const tableSection = document.querySelector('.table-section');
      if (tableSection) {
        const rect = tableSection.getBoundingClientRect();
        if (rect.top <= 120) { // 增加判断阈值
          currentSection.value = 'database';
        } else {
          currentSection.value = 'home';
        }
      }
    };

    // 添加排序处理函数
    const handleSortChange = ({ prop, order }) => {
      sortBy.value = prop;
      sortOrder.value = order;
    };

    const scrollToGenomeData = () => {
      if (genomeDataSection.value) {
        genomeDataSection.value.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }
    };

    // 添加Assembly Level筛选逻辑
    const levelFilter = ref('All');
    const applyFilters = () => {
      // 重置到第一页
      currentPage.value = 1;
      
      // 可以添加一个消息提示当前筛选条件
      if (levelFilter.value === 'All') {
        ElMessage({
          message: `Showing genomes of all assembly levels`,
          type: 'info',
          duration: 2000
        });
      } else {
        ElMessage({
          message: `Showing genomes with ${levelFilter.value} assembly level`,
          type: 'success',
          duration: 2000
        });
      }
    };

    // 修改显示Tome OGT分析对话框函数
    const showTomeOGTSelection = () => {
      if (!showSelection.value) {
        // 如果还没有显示选择框，就显示选择框
        showSelection.value = true;
        selectedRows.value = [];
        ElMessage({
          message: 'Please select strains for Tome OGT analysis',
          type: 'info'
        });
      } else if (selectedRows.value.length === 0) {
        // 如果已经显示选择框但没有选择任何菌株
        ElMessage.warning('Please select at least one strain');
      } else {
        // 如果已经选择了菌株，显示Tome OGT分析对话框
        tomeOGTDialogVisible.value = true;
      }
    };

    // 修改开始Tome OGT分析函数
    const startTomeOGTAnalysis = async () => {
      if (selectedRows.value.length === 0) {
        ElMessage.warning('Please select at least one strain');
        return;
      }

      tomeAnalyzing.value = true;

      try {
        // 显示开始分析提示
        ElMessage({
          message: `Preparing to analyze ${selectedRows.value.length} strain(s) with Tome OGT...`,
          type: 'info',
          duration: 3000
        });

        // 收集选中菌株的assembly_accession
        const selectedAccessions = selectedRows.value.map(row => row.assembly_accession);
        
        // 调用API进行批量分析
        const response = await axios.post('/api/tools/tome/batch_analyze/', {
          assembly_accessions: selectedAccessions
        });
        
        if (response.data.status === 'success') {
          ElMessage.success('Batch analysis request submitted, redirecting to results page');
          
          // 将批量分析的结果保存到localStorage，以便在TomeOGT.vue中获取
          localStorage.setItem('tome_batch_job_id', response.data.batch_job_id);
          localStorage.setItem('tome_batch_strains', JSON.stringify(
            selectedRows.value.map(row => ({
              assembly_accession: row.assembly_accession,
              organism_name: row.organism_name,
              assembly_name: row.assembly_name
            }))
          ));
          
          // 跳转到TomeOGT页面 - 修改为强制刷新的方式
          setTimeout(() => {
            window.location.replace('/tools/tome');
          }, 1000);
        } else {
          throw new Error(response.data.message || 'Batch analysis request failed');
        }
      } catch (error) {
        console.error('Tome OGT analysis failed:', error);
        ElMessage.error(`Analysis failed: ${error.message || 'Unknown error'}`);
      } finally {
        tomeAnalyzing.value = false;
        tomeOGTDialogVisible.value = false;
      }
    };

    onMounted(() => {
      loadData();
      // 检查URL中的hash
      if (window.location.hash === '#genome') {
        setTimeout(() => {
          const element = document.querySelector('.table-section');
          if (element) {
            const offset = 80; // 为导航栏预留空间
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;
            
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }
        }, 100);
      }
      
      // 添加hash变化监听
      window.addEventListener('hashchange', () => {
        if (window.location.hash === '#genome') {
          const element = document.querySelector('.table-section');
          if (element) {
            const offset = 80;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;
            
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }
        }
      });
      
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('scroll', handlePageScroll);
    });

    onUnmounted(() => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', handlePageScroll);
      // 移除事件监听
      window.removeEventListener('hashchange', () => {});
    });

    return {
      searchQuery,
      methanogens,
      loading,
      currentPage,
      pageSize,
      isScrolled,
      dialogVisible,
      selectedAssemblyId,
      filteredMethanogens,
      paginatedData,
      Search,
      Calendar,
      Download,
      View,
      DataLine,
      handleSearch,
      quickSearch,
      handleSizeChange,
      handleCurrentChange,
      getCurrentDate,
      formatDate,
      formatNumber,
      getStrainInfo,
      getAssemblyLevelType,
      viewDetail,
      headerStyle,
      cellStyle,
      errorMessage,
      handleDialogClose,
      batchDownloadDialogVisible,
      selectedFileTypes,
      batchDownloading,
      showBatchDownloadDialog,
      startBatchDownload,
      showSelection,
      selectedRows,
      handleSelectionChange,
      carouselImages,
      currentSection,
      scrollToDatabase,
      sortBy,
      sortOrder,
      handleSortChange,
      genomeDataSection,
      scrollToGenomeData,
      levelFilter,
      applyFilters,
      tomeOGTDialogVisible,
      tomeAnalyzing,
      showTomeOGTSelection,
      startTomeOGTAnalysis,
      plasmidCount,
      virusCount,
    };
  }
}
</script>

<style>
/* 导入字体 */
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Noto+Sans+SC:wght@300;400;500;700&display=swap');

/* 全局变量 */
:root {
  --primary-color: #2563eb;
  --primary-light: #3b82f6;
  --primary-dark: #1d4ed8;
  --secondary-color: #64748b;
  --accent-color: #0ea5e9;
  --background-color: #f8fafc;
  --surface-color: #ffffff;
  --text-primary: #1e293b;
  --text-secondary: #64748b;
  --border-radius: 12px;
  --transition-speed: 0.3s;
  --shadow-sm: 0 1px 3px rgba(0,0,0,0.1);
  --shadow-md: 0 4px 6px -1px rgba(0,0,0,0.1);
  --shadow-lg: 0 10px 15px -3px rgba(0,0,0,0.1);
}

/* 基础样式 */
body {
  font-family: 'Inter', 'Noto Sans SC', sans-serif;
  background-color: var(--background-color);
  color: var(--text-primary);
  line-height: 1.5;
}

/* 导航栏样式 */
.navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  transition: all var(--transition-speed);
}

.navbar-scrolled {
  box-shadow: var(--shadow-md);
}

.nav-content {
  max-width: 1280px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-color);
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 2rem;  /* 统一导航项之间的间距 */
}

.nav-links a {
  color: var(--text-primary);
  text-decoration: none;
  font-weight: 500;
  transition: color var(--transition-speed);
  padding: 0.5rem 0;
  text-align: left;  /* 左对齐文本 */
  min-width: 80px;   /* 设置最小宽度 */
}

.nav-links a:hover {
  color: var(--primary-color);
}

.nav-links a.active {
  color: var(--primary-color);
}

/* Hero 区域样式 */
.hero-section {
  position: relative;
  padding: 0;
  margin-bottom: 80px;
}

.hero-content {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  max-width: 1280px;
  z-index: 2;
  padding: 0 20px;
}

.main-title {
  font-size: 3rem;
  font-weight: 700;
  color: white;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
}

.subtitle {
  font-size: 1.5rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
}

.search-container {
  max-width: 800px;
  margin: 0 auto;
}

.search-box {
  margin-bottom: 1rem;
}

.search-box .el-input {
  --el-input-bg-color: rgba(255, 255, 255, 0.95);
  --el-input-border-color: transparent;
  --el-input-hover-border-color: var(--primary-color);
  --el-input-focus-border-color: var(--primary-color);
}

.search-tags {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
}

.search-tags .el-tag {
  background: rgba(255, 255, 255, 0.9);
  border: none;
  cursor: pointer;
  transition: all 0.3s;
}

.search-tags .el-tag:hover {
  background: white;
  transform: translateY(-2px);
}

/* 自定义轮播箭头样式 */
.el-carousel__arrow {
  width: 50px;
  height: 50px;
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  font-size: 24px;
  transition: all 0.3s;
}

.el-carousel__arrow:hover {
  background-color: rgba(255, 255, 255, 0.95);
  transform: scale(1.1);
}

/* 数据统计卡片样式 */
.stats-section {
  position: relative;
  z-index: 2; /* 提高层级 */
  margin-top: -60px; /* 向上偏移 */
  padding: 0 20px;
  margin-bottom: 40px;
}

.stats-grid {
  max-width: 1280px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.stat-card {
  background: var(--surface-color);
  border-radius: var(--border-radius);
  padding: 24px;
  box-shadow: var(--shadow-lg);
  transition: transform var(--transition-speed);
  display: flex;
  align-items: center;
  gap: 1rem;
  position: relative; /* 添加相对定位 */
  z-index: 3; /* 提高层级 */
}

.stat-card:hover {
  transform: translateY(-5px);
}

.stat-icon {
  font-size: 2rem;
  color: var(--primary-color);
}

.stat-content h3 {
  font-size: 1rem;
  color: var(--text-secondary);
  margin: 0;
}

.stat-number {
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0.5rem 0;
}

.stat-description {
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin: 0;
}

/* 表格区域样式 */
.table-section {
  position: relative;
  z-index: 2;
  background: var(--background-color);
  padding: 20px;
  margin-top: 20px;
}

.table-container {
  background: var(--surface-color);
  border-radius: var(--border-radius);
  box-shadow: var(--shadow-sm);
  padding: 24px;
  margin-bottom: 40px;
  position: relative;
  z-index: 2;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.table-header h2 {
  margin: 0;
  font-size: 1.5rem;
  color: var(--text-primary);
}

/* 表格样式优化 */
.el-table {
  --el-table-border-color: #edf2f7;
  --el-table-header-background-color: #f8fafc;
  font-family: 'Inter', 'Noto Sans SC', sans-serif;
  margin-bottom: 20px; /* 添加表格底部间距 */
}

.detail-expand {
  padding: 1rem;
}

.detail-section {
  margin-bottom: 1rem;
}

.detail-section h4 {
  color: var(--text-secondary);
  margin: 0 0 0.5rem 0;
}

.organism-name {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.name-text {
  color: var(--primary-color);
  font-weight: 500;
}

.level-tag {
  font-size: 0.75rem;
}

/* 分页容器样式 */
.pagination-container {
  margin-top: 24px;
  padding: 16px 0;
  display: flex;
  justify-content: flex-end;
  background: #ffffff; /* 添加背景色 */
  position: sticky; /* 使分页固定在底部 */
  bottom: 0;
  z-index: 2;
}

/* 响应式设计 */
@media (max-width: 1024px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .nav-links {
    display: none;
  }
  
  .main-title {
    font-size: 2rem;
  }
  
  .search-container {
    padding: 0 1rem;
  }
  
  .table-container {
    padding: 1rem;
  }
}

/* 动画效果 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.detail-link {
  color: #409EFF;
  cursor: pointer;
  text-decoration: none;
}

.detail-link:hover {
  text-decoration: underline;
}

/* 添加错误提示样式 */
.error-message {
  background-color: #fef0f0;
  color: #f56c6c;
  padding: 10px;
  margin-bottom: 15px;
  border-radius: 4px;
  font-size: 14px;
}

.batch-download-content {
  padding: 20px 0;
}

.download-title {
  font-size: 16px;
  font-weight: 500;
  color: #303133;
  margin: 0 0 20px 0;
}

.download-options {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.download-options .el-checkbox {
  margin-right: 0;
  padding: 10px;
  border-radius: 4px;
  transition: background-color 0.3s;
}

.download-options .el-checkbox:hover {
  background-color: #f5f7fa;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 20px;
}

/* 页脚样式 */
.footer {
  background: linear-gradient(to bottom, #1e293b, #0f172a);
  color: #f8fafc;
  padding: 60px 0 20px;
  margin-top: 0;
  position: relative;
  z-index: 0;
  border-top: 1px solid #334155;
}

.footer-content {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 20px;
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 40px;
}

.footer-section h3 {
  color: #f1f5f9;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 20px;
  position: relative;
  padding-bottom: 10px;
}

.footer-section h3::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 40px;
  height: 2px;
  background: linear-gradient(to right, #60a5fa, #3b82f6);
  border-radius: 2px;
}

.footer-section p {
  color: #cbd5e1;
  line-height: 1.6;
  margin-bottom: 16px;
}

.footer-section a {
  color: #60a5fa;
  text-decoration: none;
  transition: color 0.3s;
  font-weight: 500;
}

.footer-section a:hover {
  color: #93c5fd;
}

.footer-bottom {
  max-width: 1280px;
  margin: 40px auto 0;
  padding: 20px 20px 0;
  border-top: 1px solid #334155;
  text-align: center;
}

.footer-bottom p {
  color: #94a3b8;
  font-size: 14px;
}

/* 调整内容区域的样式 */
.content-section {
  min-height: calc(100vh - 400px);
  padding: 40px 20px;
  margin-bottom: 60px; /* 添加底部间距 */
}

.table-container {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: var(--shadow-sm);
  padding: 24px;
  margin-bottom: 40px;
  position: relative; /* 添加相对定位 */
  z-index: 1; /* 确保表格在页脚之上 */
}

/* 调整分页容器的样式 */
.pagination-container {
  margin-top: 24px;
  padding: 16px 0;
  display: flex;
  justify-content: flex-end;
  background: #ffffff; /* 添加背景色 */
  position: sticky; /* 使分页固定在底部 */
  bottom: 0;
  z-index: 2;
}

/* 调整页脚样式 */
.footer {
  background-color: #1e293b;
  color: #f8fafc;
  padding: 60px 0 20px;
  margin-top: 0; /* 移除顶部边距 */
  position: relative;
  z-index: 0;
}

/* 调整表格区域样式 */
.table-section {
  position: relative;
  z-index: 1;
  background: #ffffff;
  margin-bottom: 60px; /* 添加底部间距 */
}

/* 调整表格高度 */
.el-table {
  margin-bottom: 20px; /* 添加表格底部间距 */
}

/* 修改轮播图样式 */
.carousel-container {
  width: 100%;
  height: 600px;
  overflow: hidden;
  background-color: #1e293b;
  position: relative; /* 添加相对定位 */
  margin-top: 77px;
}

.carousel-content {
  width: 100%;
  height: 100%;
  position: relative;
  transition: all 0.5s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
}

.carousel-content::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.5) 0%,
    rgba(0, 0, 0, 0.7) 100%
  );
  pointer-events: none;
}

.hero-section {
  position: relative;
  padding: 0;
}

.hero-content {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  max-width: 1280px;
  z-index: 2;
  padding: 0 20px;
}

.main-title {
  font-size: 3rem;
  font-weight: 700;
  color: white;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
}

.subtitle {
  font-size: 1.5rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
}

.search-container {
  max-width: 800px;
  margin: 0 auto;
}

.search-box {
  margin-bottom: 1rem;
}

.search-box .el-input {
  --el-input-bg-color: rgba(255, 255, 255, 0.95);
  --el-input-border-color: transparent;
  --el-input-hover-border-color: var(--primary-color);
  --el-input-focus-border-color: var(--primary-color);
}

.search-tags {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
}

.search-tags .el-tag {
  background: rgba(255, 255, 255, 0.9);
  border: none;
  cursor: pointer;
  transition: all 0.3s;
}

.search-tags .el-tag:hover {
  background: white;
  transform: translateY(-2px);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .carousel-container {
    height: 400px;
  }
  
  .main-title {
    font-size: 2rem;
  }
  
  .subtitle {
    font-size: 1.2rem;
  }
}

/* 添加轮播指示器样式 */
.el-carousel__indicators {
  bottom: 20px;
}

.el-carousel__indicator {
  padding: 12px 4px;
}

.el-carousel__button {
  width: 30px;
  height: 3px;
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 3px;
  transition: all 0.3s;
}

.el-carousel__indicator.is-active .el-carousel__button {
  background-color: #fff;
}

/* 添加下拉菜单样式 */
.dropdown {
  position: relative;
  display: inline-block;
}

.dropdown > a {
  display: inline-block;
  padding: 0.5rem 0;
  text-align: left;
}

.dropdown-content {
  position: absolute;
  top: calc(100% + 5px);
  left: 0;
  min-width: 100px;  /* 减小最小宽度 */
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  overflow: hidden;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-10px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1001;
}

.dropdown-content a {
  color: var(--text-primary);
  padding: 8px 12px;  /* 减小左右内边距 */
  text-decoration: none;
  display: block;
  margin: 0 !important;
  font-weight: 500;
  font-size: 0.95rem;
  text-align: left;
  white-space: nowrap;
  transition: all 0.2s ease;
}

.dropdown:hover .dropdown-content {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.dropdown-content a:hover {
  background: var(--primary-color);
  color: white;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .dropdown-content {
    position: static;
    transform: none;
    opacity: 0;
    visibility: hidden;
    background: transparent;
    box-shadow: none;
    min-width: auto;
  }
  
  .dropdown-content::before {
    display: none;
  }
  
  .dropdown:hover .dropdown-content {
    transform: none;
  }
  
  .dropdown-content a {
    padding: 8px 0;
    color: var(--text-primary);
  }
}

.dropdown:hover .dropdown-content {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.assembly-level-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.assembly-level-cell .level-tag {
  margin-left: auto;
}

/* 添加表头排序图标的样式 */
.el-table .cell {
  display: flex !important;
  align-items: center !important;
}

.el-table .caret-wrapper {
  margin-left: 6px !important;
}

.batchgeo-map {
  margin-bottom: 20px; /* 添加一些底部间距 */
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
}

.search-box {
  flex: 1;
  min-width: 200px;
}

.filter-options {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.filter-label {
  font-weight: 500;
  color: var(--text-secondary);
  margin-right: 4px;
}

@media (max-width: 768px) {
  .toolbar {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .search-box {
    width: 100%;
    margin-bottom: 12px;
  }
  
  .filter-options {
    width: 100%;
    overflow-x: auto;
    padding-bottom: 4px;
  }
}
</style>